import React, { createContext, useContext } from 'react';

export type PresenceHexOutline = {
	/** Desired visible outer outline width in CSS pixels. */
	width: number;
	/** Optional refined SVG viewport height in CSS pixels. */
	svgHeight?: number;
	/** Optional center stroke, kept separate from the outer outline. */
	centerStrokeColor?: string;
	centerStrokeWidth?: number;
};

export const PresenceHexOutlineContext: React.Context<PresenceHexOutline | undefined> =
	createContext<PresenceHexOutline | undefined>(undefined);

export const usePresenceSvgHeight = (size: number): number => {
	const outline = useContext(PresenceHexOutlineContext);
	return outline?.svgHeight ?? size;
};

type PresenceHexArtworkProps = {
	d: string;
	fill: string;
	stroke?: string;
	strokeWidth?: number;
	viewBox: string;
	size: number;
};

/**
 * Renders a theme outline behind the original fill so its inward half is covered and the fill
 * keeps its original geometry. An optional center stroke is painted separately on top.
 */
export const PresenceHexArtwork = ({
	d,
	fill,
	stroke,
	strokeWidth,
	viewBox,
	size,
}: PresenceHexArtworkProps): React.JSX.Element => {
	const outline = useContext(PresenceHexOutlineContext);
	if (!outline) {
		return <path d={d} fill={fill} stroke={stroke} strokeWidth={strokeWidth} />;
	}

	const viewBoxWidth = Number(viewBox.trim().split(/\s+/)[2]);
	const toSvgStrokeWidth = (cssPixels: number): number => (cssPixels * viewBoxWidth) / size;
	const backingStrokeWidth = toSvgStrokeWidth(outline.width * 2);

	return (
		<>
			<path
				data-presence-layer="outline"
				d={d}
				fill={stroke}
				stroke={stroke}
				strokeWidth={backingStrokeWidth}
			/>
			<path data-presence-layer="fill" d={d} fill={fill} />
			{outline.centerStrokeColor && outline.centerStrokeWidth ? (
				<path
					data-presence-layer="center-stroke"
					d={d}
					fill="none"
					stroke={outline.centerStrokeColor}
					strokeWidth={toSvgStrokeWidth(outline.centerStrokeWidth)}
				/>
			) : null}
		</>
	);
};
